import { apiClient, withConflictRetry } from './api';
import {
  Apartment,
  RegisterApartmentRequest,
  UpdateApartmentRequest,
  UpdateApartmentAddressRequest,
  UpdateApartmentDetailsRequest,
  UpdateApartmentPricingRequest,
  UpdateFeaturesRequest,
  UpdatePhotosRequest,
} from '../types/apartment';

export const apartmentService = {
  // Get caller's apartments with full detail (ownership-filtered)
  async getAll(): Promise<Apartment[]> {
    const response = await apiClient.get<Apartment[]>('/admin/apartments/mine/full');
    return response.data;
  },

  // Get single apartment by ID
  async getById(id: string): Promise<Apartment> {
    const response = await apiClient.get<Apartment>(`/apartments/${id}`);
    return response.data;
  },

  // Register new apartment
  async create(data: RegisterApartmentRequest): Promise<Apartment> {
    const response = await apiClient.post<Apartment>('/apartments', data);
    return response.data;
  },

  // Full update of apartment
  async update(id: string, data: UpdateApartmentRequest): Promise<Apartment> {
    return withConflictRetry(
      () => apiClient.put<Apartment>(`/apartments/${id}`, data).then(r => r.data),
      () => this.getById(id)
    );
  },

  // Update apartment address
  async updateAddress(id: string, data: UpdateApartmentAddressRequest): Promise<Apartment> {
    return withConflictRetry(
      () => apiClient.patch<Apartment>(`/apartments/${id}/address`, data).then(r => r.data),
      () => this.getById(id)
    );
  },

  // Update apartment details
  async updateDetails(id: string, data: UpdateApartmentDetailsRequest): Promise<Apartment> {
    return withConflictRetry(
      () => apiClient.patch<Apartment>(`/apartments/${id}/details`, data).then(r => r.data),
      () => this.getById(id)
    );
  },

  // Update apartment pricing
  async updatePricing(id: string, data: UpdateApartmentPricingRequest): Promise<Apartment> {
    return withConflictRetry(
      () => apiClient.patch<Apartment>(`/apartments/${id}/pricing`, data).then(r => r.data),
      () => this.getById(id)
    );
  },

  // Update features (replaces all)
  async updateFeatures(id: string, data: UpdateFeaturesRequest): Promise<Apartment> {
    return withConflictRetry(
      () => apiClient.put<Apartment>(`/apartments/${id}/features`, data).then(r => r.data),
      () => this.getById(id)
    );
  },

  // Update photos (bulk replace/reorder)
  async updatePhotos(id: string, data: UpdatePhotosRequest): Promise<Apartment> {
    return withConflictRetry(
      () => apiClient.put<Apartment>(`/apartments/${id}/photos`, data).then(r => r.data),
      () => this.getById(id)
    );
  },

  // Publish apartment
  async publish(id: string): Promise<Apartment> {
    return withConflictRetry(
      () => apiClient.post<Apartment>(`/apartments/${id}/publish`).then(r => r.data),
      () => this.getById(id)
    );
  },

  // Unpublish apartment
  async unpublish(id: string): Promise<Apartment> {
    return withConflictRetry(
      () => apiClient.post<Apartment>(`/apartments/${id}/unpublish`).then(r => r.data),
      () => this.getById(id)
    );
  },

  // Ranking — viewOrder management
  // No withConflictRetry: the backend retries internally (up to 3 times) before returning 409.
  async rankUp(id: string): Promise<Apartment> {
    const response = await apiClient.post<Apartment>(`/admin/apartments/${id}/rank/up`);
    return response.data;
  },

  async rankDown(id: string): Promise<Apartment> {
    const response = await apiClient.post<Apartment>(`/admin/apartments/${id}/rank/down`);
    return response.data;
  },

  async rankToTop(id: string): Promise<Apartment> {
    const response = await apiClient.post<Apartment>(`/admin/apartments/${id}/rank/top`);
    return response.data;
  },

  // Delete apartment
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/apartments/${id}`);
  },

  // Legacy methods for backward compatibility
  async addFeature(id: string, feature: string): Promise<Apartment> {
    // Get current apartment
    const apartment = await this.getById(id);
    const currentFeatures = apartment.details.features;

    // Add feature if not already present
    if (!currentFeatures.includes(feature)) {
      return this.updateFeatures(id, {
        features: [...currentFeatures, feature],
      });
    }

    return apartment;
  },

  async removeFeature(id: string, feature: string): Promise<Apartment> {
    // Get current apartment
    const apartment = await this.getById(id);
    const currentFeatures = apartment.details.features;

    // Remove feature
    return this.updateFeatures(id, {
      features: currentFeatures.filter(f => f !== feature),
    });
  },

  async addPhoto(id: string): Promise<Apartment> {
    // This is deprecated - use photoService.confirmPhoto instead
    console.warn('apartmentService.addPhoto is deprecated. Use photoService.confirmPhoto instead.');
    const apartment = await this.getById(id);
    return apartment;
  },

  async removePhoto(id: string, photoKey: string): Promise<Apartment> {
    // Delete via photo-specific endpoint
    const response = await apiClient.delete<Apartment>(`/apartments/${id}/photos/${encodeURIComponent(photoKey)}`);
    return response.data;
  },
};
